import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

interface QuickMessage {
  id: string;
  title: string;
  content: string;
  category: string;
}

interface QuickMessagesProps {
  onSendMessage: (message: string) => void;
  onInsertMessage: (message: string) => void;
  disabled?: boolean;
}

const QuickMessages: React.FC<QuickMessagesProps> = ({ 
  onSendMessage, 
  onInsertMessage, 
  disabled = false 
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [language, setLanguage] = useState<string>('en');
  const [quickMessages, setQuickMessages] = useState<QuickMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const translations = {
    en: {
      messages: [
        // Welcome/Intro
        { id: '1', title: 'Welcome Message', content: 'Hello! Thank you for contacting support. How can I help you today?', category: 'welcome' },
        { id: '2', title: 'Ready to Assist', content: 'Hi! I\'m here to assist you. Can you describe the issue you\'re facing?', category: 'welcome' },
        { id: '3', title: 'Technical Support', content: 'I\'m ready to help with your technical issue. Let\'s get this resolved quickly.', category: 'welcome' },
        // Troubleshooting
        { id: '4', title: 'Device Restart', content: 'Can you please try restarting the device and let me know if the issue persists?', category: 'troubleshooting' },
        { id: '5', title: 'Serial Number Request', content: 'Please confirm the serial number of the device for verification.', category: 'troubleshooting' },
        { id: '6', title: 'Cable Check', content: 'I recommend checking the cable connections and power supply first.', category: 'troubleshooting' },
        { id: '7', title: 'Network Connection', content: 'Can you please check if the device is properly connected to your network?', category: 'troubleshooting' },
        { id: '8', title: 'Software Update', content: 'Let\'s check if your device firmware is up to date. Please navigate to Settings > About.', category: 'troubleshooting' },
        // Setup
        { id: '9', title: 'Setup Instructions', content: 'I\'ll guide you through the setup process step by step. Are you ready to begin?', category: 'setup' },
        { id: '10', title: 'Configuration Help', content: 'Let\'s configure your device settings to ensure optimal performance.', category: 'setup' },
        // Connection
        { id: '11', title: 'Connection Troubleshoot', content: 'Let\'s troubleshoot the connection issue. Can you see any error messages on your device?', category: 'connection' },
        { id: '12', title: 'Wi-Fi Setup', content: 'I\'ll help you connect your device to Wi-Fi. First, make sure you have your network password ready.', category: 'connection' },
        // Escalation
        { id: '13', title: 'Reviewing Issue', content: 'Let me review the issue and get back to you shortly.', category: 'escalation' },
        { id: '14', title: 'Technical Team Escalation', content: 'I\'m escalating this to our technical team. Please stay tuned.', category: 'escalation' },
        { id: '15', title: 'Patience Request', content: 'Thanks for your patience - we\'re still working on this.', category: 'escalation' },
        { id: '16', title: 'Specialist Consult', content: 'I\'m consulting with a specialist to provide you with the best solution. This may take a few minutes.', category: 'escalation' },
        // Closure
        { id: '17', title: 'Resolution Confirmation', content: 'Your ticket has been resolved. Please confirm everything is working as expected.', category: 'closure' },
        { id: '18', title: 'Ticket Closure', content: 'We\'re closing this ticket for now, but feel free to reopen if needed.', category: 'closure' },
        { id: '19', title: 'Follow-up Offer', content: 'Is there anything else I can help you with today?', category: 'closure' },
        { id: '20', title: 'Satisfaction Check', content: 'How would you rate your experience with our support today?', category: 'closure' },
        // General
        { id: '21', title: 'Information Gathering', content: 'To better assist you, can you provide more details about when this issue started?', category: 'general' },
        { id: '22', title: 'Screenshot Request', content: 'If possible, could you please share a screenshot of what you\'re seeing?', category: 'general' },
        { id: '23', title: 'Remote Assistance', content: 'Would you be comfortable with a remote assistance session to resolve this quickly?', category: 'general' }
      ]
    },
    es: {
      messages: [
        // Welcome/Intro
        { id: '1', title: 'Mensaje de Bienvenida', content: '¡Hola! Gracias por contactar a soporte. ¿Cómo puedo ayudarte hoy?', category: 'welcome' },
        { id: '2', title: 'Listo para Asistir', content: '¡Hola! Estoy aquí para asistirte. ¿Puedes describir el problema que estás enfrentando?', category: 'welcome' },
        { id: '3', title: 'Soporte Técnico', content: 'Estoy listo para ayudar con tu problema técnico. Resolvamos esto rápidamente.', category: 'welcome' },
        // Troubleshooting
        { id: '4', title: 'Reinicio del Dispositivo', content: '¿Puedes intentar reiniciar el dispositivo y avisarme si el problema persiste?', category: 'troubleshooting' },
        { id: '5', title: 'Solicitud de Número de Serie', content: 'Por favor confirma el número de serie del dispositivo para verificación.', category: 'troubleshooting' },
        { id: '6', title: 'Verificación de Cables', content: 'Recomiendo verificar las conexiones de cables y la fuente de alimentación primero.', category: 'troubleshooting' },
        { id: '7', title: 'Conexión de Red', content: '¿Puedes verificar si el dispositivo está conectado correctamente a tu red?', category: 'troubleshooting' },
        { id: '8', title: 'Actualización de Software', content: 'Verifiquemos si el firmware de tu dispositivo está actualizado. Ve a Configuración > Acerca de.', category: 'troubleshooting' },
        // Setup
        { id: '9', title: 'Instrucciones de Configuración', content: 'Te guiaré paso a paso en el proceso de configuración. ¿Estás listo para comenzar?', category: 'setup' },
        { id: '10', title: 'Ayuda de Configuración', content: 'Configuremos los ajustes de tu dispositivo para asegurar un rendimiento óptimo.', category: 'setup' },
        // Connection
        { id: '11', title: 'Solución de Problemas de Conexión', content: 'Solucionemos el problema de conexión. ¿Ves algún mensaje de error en tu dispositivo?', category: 'connection' },
        { id: '12', title: 'Configuración Wi-Fi', content: 'Te ayudaré a conectar tu dispositivo al Wi-Fi. Primero, asegúrate de tener la contraseña de tu red lista.', category: 'connection' },
        // Escalation
        { id: '13', title: 'Revisando el Problema', content: 'Déjame revisar el problema y te responderé en breve.', category: 'escalation' },
        { id: '14', title: 'Escalación al Equipo Técnico', content: 'Estoy escalando esto a nuestro equipo técnico. Por favor mantente atento.', category: 'escalation' },
        { id: '15', title: 'Solicitud de Paciencia', content: 'Gracias por tu paciencia - seguimos trabajando en esto.', category: 'escalation' },
        { id: '16', title: 'Consulta con Especialista', content: 'Estoy consultando con un especialista para brindarte la mejor solución. Esto puede tomar unos minutos.', category: 'escalation' },
        // Closure
        { id: '17', title: 'Confirmación de Resolución', content: 'Tu ticket ha sido resuelto. Por favor confirma que todo funciona como se espera.', category: 'closure' },
        { id: '18', title: 'Cierre del Ticket', content: 'Estamos cerrando este ticket por ahora, pero no dudes en reabrirlo si es necesario.', category: 'closure' },
        { id: '19', title: 'Oferta de Seguimiento', content: '¿Hay algo más en lo que pueda ayudarte hoy?', category: 'closure' },
        { id: '20', title: 'Verificación de Satisfacción', content: '¿Cómo calificarías tu experiencia con nuestro soporte hoy?', category: 'closure' },
        // General
        { id: '21', title: 'Recopilación de Información', content: 'Para asistirte mejor, ¿puedes proporcionar más detalles sobre cuándo comenzó este problema?', category: 'general' },
        { id: '22', title: 'Solicitud de Captura de Pantalla', content: 'Si es posible, ¿podrías compartir una captura de pantalla de lo que estás viendo?', category: 'general' },
        { id: '23', title: 'Asistencia Remota', content: '¿Te sentirías cómodo con una sesión de asistencia remota para resolver esto rápidamente?', category: 'general' }
      ]
    },
    fr: {
      messages: [
        // Welcome/Intro
        { id: '1', title: 'Message de Bienvenue', content: 'Bonjour ! Merci de contacter le support. Comment puis-je vous aider aujourd\'hui ?', category: 'welcome' },
        { id: '2', title: 'Prêt à Assister', content: 'Salut ! Je suis là pour vous aider. Pouvez-vous décrire le problème que vous rencontrez ?', category: 'welcome' },
        { id: '3', title: 'Support Technique', content: 'Je suis prêt à vous aider avec votre problème technique. Résolvons cela rapidement.', category: 'welcome' },
        // Troubleshooting
        { id: '4', title: 'Redémarrage de l\'Appareil', content: 'Pouvez-vous essayer de redémarrer l\'appareil et me dire si le problème persiste ?', category: 'troubleshooting' },
        { id: '5', title: 'Demande de Numéro de Série', content: 'Veuillez confirmer le numéro de série de l\'appareil pour vérification.', category: 'troubleshooting' },
        { id: '6', title: 'Vérification des Câbles', content: 'Je recommande de vérifier les connexions de câbles et l\'alimentation en premier.', category: 'troubleshooting' },
        { id: '7', title: 'Connexion Réseau', content: 'Pouvez-vous vérifier si l\'appareil est correctement connecté à votre réseau ?', category: 'troubleshooting' },
        { id: '8', title: 'Mise à Jour Logicielle', content: 'Vérifions si le firmware de votre appareil est à jour. Allez dans Paramètres > À propos.', category: 'troubleshooting' },
        // Setup
        { id: '9', title: 'Instructions de Configuration', content: 'Je vais vous guider étape par étape dans le processus de configuration. Êtes-vous prêt à commencer ?', category: 'setup' },
        { id: '10', title: 'Aide à la Configuration', content: 'Configurons les paramètres de votre appareil pour assurer une performance optimale.', category: 'setup' },
        // Connection
        { id: '11', title: 'Dépannage de Connexion', content: 'Dépannons le problème de connexion. Voyez-vous des messages d\'erreur sur votre appareil ?', category: 'connection' },
        { id: '12', title: 'Configuration Wi-Fi', content: 'Je vais vous aider à connecter votre appareil au Wi-Fi. D\'abord, assurez-vous d\'avoir votre mot de passe réseau prêt.', category: 'connection' },
        // Escalation
        { id: '13', title: 'Examen du Problème', content: 'Laissez-moi examiner le problème et je reviendrai vers vous sous peu.', category: 'escalation' },
        { id: '14', title: 'Escalade vers l\'Équipe Technique', content: 'J\'escalade ceci à notre équipe technique. Veuillez rester à l\'écoute.', category: 'escalation' },
        { id: '15', title: 'Demande de Patience', content: 'Merci pour votre patience - nous travaillons encore sur ceci.', category: 'escalation' },
        { id: '16', title: 'Consultation Spécialisée', content: 'Je consulte un spécialiste pour vous fournir la meilleure solution. Cela peut prendre quelques minutes.', category: 'escalation' },
        // Closure
        { id: '17', title: 'Confirmation de Résolution', content: 'Votre ticket a été résolu. Veuillez confirmer que tout fonctionne comme prévu.', category: 'closure' },
        { id: '18', title: 'Fermeture du Ticket', content: 'Nous fermons ce ticket pour le moment, mais n\'hésitez pas à le rouvrir si nécessaire.', category: 'closure' },
        { id: '19', title: 'Offre de Suivi', content: 'Y a-t-il autre chose avec quoi je peux vous aider aujourd\'hui ?', category: 'closure' },
        { id: '20', title: 'Vérification de Satisfaction', content: 'Comment évalueriez-vous votre expérience avec notre support aujourd\'hui ?', category: 'closure' },
        // General
        { id: '21', title: 'Collecte d\'Informations', content: 'Pour mieux vous aider, pouvez-vous fournir plus de détails sur quand ce problème a commencé ?', category: 'general' },
        { id: '22', title: 'Demande de Capture d\'Écran', content: 'Si possible, pourriez-vous partager une capture d\'écran de ce que vous voyez ?', category: 'general' },
        { id: '23', title: 'Assistance à Distance', content: 'Seriez-vous à l\'aise avec une session d\'assistance à distance pour résoudre ceci rapidement ?', category: 'general' }
      ]
    },
    de: {
      messages: [
        // Welcome/Intro
        { id: '1', title: 'Willkommensnachricht', content: 'Hallo! Vielen Dank, dass Sie den Support kontaktiert haben. Wie kann ich Ihnen heute helfen?', category: 'welcome' },
        { id: '2', title: 'Bereit zu Helfen', content: 'Hallo! Ich bin hier, um Ihnen zu helfen. Können Sie das Problem beschreiben, mit dem Sie konfrontiert sind?', category: 'welcome' },
        { id: '3', title: 'Technischer Support', content: 'Ich bin bereit, bei Ihrem technischen Problem zu helfen. Lassen Sie uns das schnell lösen.', category: 'welcome' },
        // Troubleshooting
        { id: '4', title: 'Geräte-Neustart', content: 'Können Sie bitte versuchen, das Gerät neu zu starten und mir mitteilen, ob das Problem weiterhin besteht?', category: 'troubleshooting' },
        { id: '5', title: 'Seriennummer-Anfrage', content: 'Bitte bestätigen Sie die Seriennummer des Geräts zur Verifizierung.', category: 'troubleshooting' },
        { id: '6', title: 'Kabel-Überprüfung', content: 'Ich empfehle, zuerst die Kabelverbindungen und die Stromversorgung zu überprüfen.', category: 'troubleshooting' },
        { id: '7', title: 'Netzwerkverbindung', content: 'Können Sie bitte überprüfen, ob das Gerät ordnungsgemäß mit Ihrem Netzwerk verbunden ist?', category: 'troubleshooting' },
        { id: '8', title: 'Software-Update', content: 'Lassen Sie uns überprüfen, ob die Firmware Ihres Geräts auf dem neuesten Stand ist. Gehen Sie zu Einstellungen > Info.', category: 'troubleshooting' },
        // Setup
        { id: '9', title: 'Setup-Anweisungen', content: 'Ich führe Sie Schritt für Schritt durch den Einrichtungsprozess. Sind Sie bereit zu beginnen?', category: 'setup' },
        { id: '10', title: 'Konfigurationshilfe', content: 'Lassen Sie uns die Einstellungen Ihres Geräts konfigurieren, um optimale Leistung zu gewährleisten.', category: 'setup' },
        // Connection
        { id: '11', title: 'Verbindungsfehlerbehebung', content: 'Lassen Sie uns das Verbindungsproblem beheben. Sehen Sie Fehlermeldungen auf Ihrem Gerät?', category: 'connection' },
        { id: '12', title: 'WLAN-Setup', content: 'Ich helfe Ihnen dabei, Ihr Gerät mit dem WLAN zu verbinden. Stellen Sie zunächst sicher, dass Sie Ihr Netzwerkpasswort bereit haben.', category: 'connection' },
        // Escalation
        { id: '13', title: 'Problem Überprüfen', content: 'Lassen Sie mich das Problem überprüfen und ich komme in Kürze auf Sie zurück.', category: 'escalation' },
        { id: '14', title: 'Eskalation an Technik-Team', content: 'Ich eskaliere dies an unser technisches Team. Bitte bleiben Sie dran.', category: 'escalation' },
        { id: '15', title: 'Geduld-Anfrage', content: 'Danke für Ihre Geduld - wir arbeiten noch daran.', category: 'escalation' },
        { id: '16', title: 'Spezialist-Beratung', content: 'Ich konsultiere einen Spezialisten, um Ihnen die beste Lösung zu bieten. Das kann einige Minuten dauern.', category: 'escalation' },
        // Closure
        { id: '17', title: 'Lösungsbestätigung', content: 'Ihr Ticket wurde gelöst. Bitte bestätigen Sie, dass alles wie erwartet funktioniert.', category: 'closure' },
        { id: '18', title: 'Ticket-Schließung', content: 'Wir schließen dieses Ticket vorerst, aber zögern Sie nicht, es bei Bedarf wieder zu öffnen.', category: 'closure' },
        { id: '19', title: 'Nachfrage-Angebot', content: 'Gibt es noch etwas anderes, womit ich Ihnen heute helfen kann?', category: 'closure' },
        { id: '20', title: 'Zufriedenheits-Check', content: 'Wie würden Sie Ihre Erfahrung mit unserem Support heute bewerten?', category: 'closure' },
        // General
        { id: '21', title: 'Informationssammlung', content: 'Um Ihnen besser zu helfen, können Sie mehr Details darüber geben, wann dieses Problem begann?', category: 'general' },
        { id: '22', title: 'Screenshot-Anfrage', content: 'Falls möglich, könnten Sie bitte einen Screenshot von dem teilen, was Sie sehen?', category: 'general' },
        { id: '23', title: 'Remote-Unterstützung', content: 'Wären Sie mit einer Remote-Unterstützungssitzung einverstanden, um dies schnell zu lösen?', category: 'general' }
      ]
    },
    pt: {
      messages: [
        // Welcome/Intro
        { id: '1', title: 'Mensagem de Boas-vindas', content: 'Olá! Obrigado por entrar em contato com o suporte. Como posso ajudá-lo hoje?', category: 'welcome' },
        { id: '2', title: 'Pronto para Ajudar', content: 'Oi! Estou aqui para ajudá-lo. Pode descrever o problema que está enfrentando?', category: 'welcome' },
        { id: '3', title: 'Suporte Técnico', content: 'Estou pronto para ajudar com seu problema técnico. Vamos resolver isso rapidamente.', category: 'welcome' },
        // Troubleshooting
        { id: '4', title: 'Reiniciar Dispositivo', content: 'Pode tentar reiniciar o dispositivo e me avisar se o problema persistir?', category: 'troubleshooting' },
        { id: '5', title: 'Solicitação de Número de Série', content: 'Por favor, confirme o número de série do dispositivo para verificação.', category: 'troubleshooting' },
        { id: '6', title: 'Verificação de Cabos', content: 'Recomendo verificar as conexões dos cabos e a fonte de alimentação primeiro.', category: 'troubleshooting' },
        { id: '7', title: 'Conexão de Rede', content: 'Pode verificar se o dispositivo está conectado corretamente à sua rede?', category: 'troubleshooting' },
        { id: '8', title: 'Atualização de Software', content: 'Vamos verificar se o firmware do seu dispositivo está atualizado. Vá para Configurações > Sobre.', category: 'troubleshooting' },
        // Setup
        { id: '9', title: 'Instruções de Configuração', content: 'Vou guiá-lo passo a passo no processo de configuração. Está pronto para começar?', category: 'setup' },
        { id: '10', title: 'Ajuda de Configuração', content: 'Vamos configurar as definições do seu dispositivo para garantir o desempenho ideal.', category: 'setup' },
        // Connection
        { id: '11', title: 'Solução de Problemas de Conexão', content: 'Vamos resolver o problema de conexão. Consegue ver alguma mensagem de erro no seu dispositivo?', category: 'connection' },
        { id: '12', title: 'Configuração Wi-Fi', content: 'Vou ajudá-lo a conectar seu dispositivo ao Wi-Fi. Primeiro, certifique-se de ter a senha da sua rede pronta.', category: 'connection' },
        // Escalation
        { id: '13', title: 'Revisando o Problema', content: 'Deixe-me revisar o problema e volto em breve.', category: 'escalation' },
        { id: '14', title: 'Escalação para Equipe Técnica', content: 'Estou escalando isso para nossa equipe técnica. Por favor, aguarde.', category: 'escalation' },
        { id: '15', title: 'Pedido de Paciência', content: 'Obrigado pela paciência - ainda estamos trabalhando nisso.', category: 'escalation' },
        { id: '16', title: 'Consulta com Especialista', content: 'Estou consultando um especialista para fornecer a melhor solução. Isso pode levar alguns minutos.', category: 'escalation' },
        // Closure
        { id: '17', title: 'Confirmação de Resolução', content: 'Seu ticket foi resolvido. Por favor, confirme se tudo está funcionando conforme esperado.', category: 'closure' },
        { id: '18', title: 'Fechamento do Ticket', content: 'Estamos fechando este ticket por enquanto, mas sinta-se à vontade para reabrir se necessário.', category: 'closure' },
        { id: '19', title: 'Oferta de Acompanhamento', content: 'Há mais alguma coisa com que possa ajudá-lo hoje?', category: 'closure' },
        { id: '20', title: 'Verificação de Satisfação', content: 'Como avaliaria sua experiência com nosso suporte hoje?', category: 'closure' },
        // General
        { id: '21', title: 'Coleta de Informações', content: 'Para melhor ajudá-lo, pode fornecer mais detalhes sobre quando este problema começou?', category: 'general' },
        { id: '22', title: 'Solicitação de Captura de Tela', content: 'Se possível, poderia compartilhar uma captura de tela do que está vendo?', category: 'general' },
        { id: '23', title: 'Assistência Remota', content: 'Ficaria confortável com uma sessão de assistência remota para resolver isso rapidamente?', category: 'general' }
      ]
    }
  };

  // Load quick messages from API
  useEffect(() => {
    const loadQuickMessages = async () => {
      try {
        setLoading(true);
        setError('');
        const messages = await apiService.getQuickMessages(language);
        setQuickMessages(messages);
      } catch (err) {
        console.error('Error loading quick messages:', err);
        setError('Failed to load quick messages');
        setQuickMessages([]); // Fallback to empty array
      } finally {
        setLoading(false);
      }
    };

    loadQuickMessages();
  }, [language]);

  const categoryTranslations = {
    en: {
      all: 'All Messages',
      welcome: 'Welcome',
      troubleshooting: 'Troubleshooting',
      setup: 'Device Setup',
      connection: 'Connection',
      escalation: 'Escalation',
      closure: 'Closure',
      general: 'General'
    },
    es: {
      all: 'Todos los Mensajes',
      welcome: 'Bienvenida',
      troubleshooting: 'Solución de Problemas',
      setup: 'Configuración del Dispositivo',
      connection: 'Conexión',
      escalation: 'Escalación',
      closure: 'Cierre',
      general: 'General'
    },
    fr: {
      all: 'Tous les Messages',
      welcome: 'Bienvenue',
      troubleshooting: 'Dépannage',
      setup: 'Configuration de l\'Appareil',
      connection: 'Connexion',
      escalation: 'Escalade',
      closure: 'Fermeture',
      general: 'Général'
    },
    de: {
      all: 'Alle Nachrichten',
      welcome: 'Willkommen',
      troubleshooting: 'Fehlerbehebung',
      setup: 'Geräte-Setup',
      connection: 'Verbindung',
      escalation: 'Eskalation',
      closure: 'Abschluss',
      general: 'Allgemein'
    },
    pt: {
      all: 'Todas as Mensagens',
      welcome: 'Boas-vindas',
      troubleshooting: 'Solução de Problemas',
      setup: 'Configuração do Dispositivo',
      connection: 'Conexão',
      escalation: 'Escalação',
      closure: 'Encerramento',
      general: 'Geral'
    }
  };

  const currentCategoryTranslations = categoryTranslations[language as keyof typeof categoryTranslations] || categoryTranslations.en;

  const categories = [
    { value: 'all', label: currentCategoryTranslations.all },
    { value: 'welcome', label: currentCategoryTranslations.welcome },
    { value: 'troubleshooting', label: currentCategoryTranslations.troubleshooting },
    { value: 'setup', label: currentCategoryTranslations.setup },
    { value: 'connection', label: currentCategoryTranslations.connection },
    { value: 'escalation', label: currentCategoryTranslations.escalation },
    { value: 'closure', label: currentCategoryTranslations.closure },
    { value: 'general', label: currentCategoryTranslations.general }
  ];

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' },
    { value: 'pt', label: 'Português' }
  ];

  const filteredMessages = selectedCategory === 'all' 
    ? quickMessages 
    : quickMessages.filter(msg => msg.category === selectedCategory);

  const handleSendClick = (message: QuickMessage) => {
    if (!disabled) {
      onSendMessage(message.content);
    }
  };

  const handleInsertClick = (message: QuickMessage) => {
    if (!disabled) {
      onInsertMessage(message.content);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg w-full lg:w-96" style={{ height: '450px' }}>
      <div className="p-2 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Quick Messages</h3>
          <div className="flex items-center space-x-2">
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="text-xs border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              {languages.map(lang => (
                <option key={lang.value} value={lang.value}>{lang.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full text-xs border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          {categories.map(category => (
            <option key={category.value} value={category.value}>{category.label}</option>
          ))}
        </select>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1" style={{ height: 'calc(450px - 100px)' }}>
        {loading ? (
          <div className="text-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto mb-2"></div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Loading messages...</p>
          </div>
        ) : error ? (
          <div className="text-center py-6">
            <div className="text-red-400 dark:text-red-500 mb-2">
              <svg className="mx-auto h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 13.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          </div>
        ) : filteredMessages.map((message) => (
          <div key={message.id} className="border border-gray-200 dark:border-gray-600 rounded-md p-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <div className="flex items-start justify-between gap-2">
              {/* Left side - Message content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-1">
                  <h4 className="text-xs font-medium text-gray-900 dark:text-gray-100 leading-tight truncate pr-2">{message.title}</h4>
                  <span className="text-xs px-1 py-0.5 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded text-center flex-shrink-0" style={{ fontSize: '10px' }}>
                    {(currentCategoryTranslations[message.category as keyof typeof currentCategoryTranslations] || message.category).slice(0, 3)}
                  </span>
                </div>
                
                <p className="text-xs text-gray-700 dark:text-gray-300 leading-tight" style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {message.content}
                </p>
              </div>
              
              {/* Right side - Compact buttons */}
              <div className="flex flex-col space-y-1 flex-shrink-0">
                <button
                  onClick={() => handleSendClick(message)}
                  disabled={disabled}
                  className="px-2 py-0.5 bg-primary-600 text-white rounded hover:bg-primary-700 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ fontSize: '10px', minWidth: '35px' }}
                  title="Send message"
                >
                  Send
                </button>
                <button
                  onClick={() => handleInsertClick(message)}
                  disabled={disabled}
                  className="px-2 py-0.5 bg-gray-600 text-white rounded hover:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ fontSize: '10px', minWidth: '35px' }}
                  title="Insert into message box"
                >
                  Insert
                </button>
              </div>
            </div>
          </div>
        ))}
        
        {!loading && !error && filteredMessages.length === 0 && (
          <div className="text-center py-6">
            <div className="text-gray-400 dark:text-gray-500 mb-2">
              <svg className="mx-auto h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-1.586l-4.414 4.414z" />
              </svg>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">No messages in this category</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickMessages; 